import { useMutation, useQuery } from "@tanstack/react-query";
import { useContext } from "react";
import {
  AlertBannerContext,
  API_RESPONSE_CODE,
  ALERT_BANNER_CODE,
  ALERT_BANNER_CODE_SEVERITY
} from "@kivunova/kivufrontendcommon";
import { fetchGraphQL } from "../../../Graphql/utils";
import {
  archiveFeeStructureMutation,
  createFeeStructureQuery,
  listFeeStructuresQuery
} from "../../../Graphql/querries/education/feeStructureQuery";
import {
  ArchiveFeeStructureInput,
  ArchiveFeeStructureResponse,
  CreateFeeStructureInput,
  CreateFeeStructureResponse,
  FeeStructure,
  ListFeeStructuresInput,
  ListFeeStructuresResponse
} from "../../../components/Education/Fees/types/feeStructure";
import { GraphQLData, unwrapGraphQLOperation } from "./educationGraphQLUtils";

export const useFeeStructures = (input?: ListFeeStructuresInput) => {
  const { setAlert } = useContext(AlertBannerContext);

  return useQuery({
    queryKey: ["feeStructures", input?.billingPeriodId ?? "", input?.page ?? 0],
    queryFn: async (): Promise<FeeStructure[]> => {
      const data = await fetchGraphQL<
        GraphQLData<ListFeeStructuresResponse, "listFeeStructures">,
        { input: ListFeeStructuresInput | Record<string, never> }
      >(listFeeStructuresQuery, { input: input ?? {} }, setAlert);
      const response = unwrapGraphQLOperation(data, "listFeeStructures");
      if (response.code !== API_RESPONSE_CODE.OK) {
        setAlert({
          code: ALERT_BANNER_CODE.GENERAL_ERROR,
          severity: ALERT_BANNER_CODE_SEVERITY.ERROR
        });
        return [];
      }
      return response.feeStructures ?? [];
    }
  });
};

export const useCreateFeeStructure = () => {
  const { setAlert } = useContext(AlertBannerContext);

  return useMutation({
    mutationFn: async (input: CreateFeeStructureInput) => {
      const data = await fetchGraphQL<
        GraphQLData<CreateFeeStructureResponse, "createFeeStructure">,
        { input: CreateFeeStructureInput }
      >(createFeeStructureQuery, { input }, setAlert);
      const response = unwrapGraphQLOperation(data, "createFeeStructure");
      if (response.code !== API_RESPONSE_CODE.OK || !response.feeStructure) {
        throw new Error(response.code);
      }
      return response.feeStructure;
    },
    onSuccess: () => {
      setAlert({
        code: ALERT_BANNER_CODE.SUCCESS,
        severity: ALERT_BANNER_CODE_SEVERITY.SUCCESS
      });
    }
  });
};

export const useArchiveFeeStructure = () => {
  const { setAlert } = useContext(AlertBannerContext);

  return useMutation({
    mutationFn: async (input: ArchiveFeeStructureInput) => {
      const data = await fetchGraphQL<
        GraphQLData<ArchiveFeeStructureResponse, "archiveFeeStructure">,
        { input: ArchiveFeeStructureInput }
      >(archiveFeeStructureMutation, { input }, setAlert);
      const response = unwrapGraphQLOperation(data, "archiveFeeStructure");
      if (response.code !== API_RESPONSE_CODE.OK) {
        throw new Error(response.code);
      }
      return response.feeStructure;
    },
    onSuccess: () => {
      setAlert({
        code: ALERT_BANNER_CODE.SUCCESS,
        severity: ALERT_BANNER_CODE_SEVERITY.SUCCESS
      });
    }
  });
};
